import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDataProvider, useNotify } from "ra-core";

import type { PlaneKeyError } from "../providers/commons/planeKey";
import type { CrmDataProvider } from "../providers/types";

// The signed-in user's own Plane API key. A push to Plane runs with it, so Plane
// shows that person as the work item's creator. Only "connected as <email>" ever
// reaches the browser; the key stays server-side. LCCRM-92.
export const PLANE_KEY_QUERY_KEY = ["plane_key"] as const;

// Changes only when this user saves or removes their key, and both mutations
// below write the cache themselves.
const PLANE_KEY_STALE_TIME = 5 * 60 * 1000;

export const useQ_PlaneKey_Status = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  return useQuery({
    queryKey: PLANE_KEY_QUERY_KEY,
    queryFn: () => dataProvider.getPlaneKeyStatus(),
    staleTime: PLANE_KEY_STALE_TIME,
    retry: false,
  });
};

/**
 * For a click handler that must know before it acts: resolves whether the
 * signed-in user has a Plane key, from the cache when it is fresh. Rejects when
 * the status cannot be read.
 */
export const useHasPlaneKey = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  return useCallback(async (): Promise<boolean> => {
    const status = await queryClient.fetchQuery({
      queryKey: PLANE_KEY_QUERY_KEY,
      queryFn: () => dataProvider.getPlaneKeyStatus(),
      staleTime: PLANE_KEY_STALE_TIME,
    });
    return status.connected === true;
  }, [dataProvider, queryClient]);
};

/** The message key for why a key was refused. */
export const planeKeyErrorMessage = (error: unknown): string => {
  switch ((error as PlaneKeyError | undefined)?.reason) {
    case "rejected":
      return "crm.plane.key.error_rejected";
    case "not_member":
      return "crm.plane.key.error_not_member";
    case "plane_unavailable":
      return "crm.plane.key.error_unavailable";
    default:
      return "crm.plane.key.error_generic";
  }
};

export const useM_PlaneKey_Save = ({
  onSuccess,
}: { onSuccess?: () => void } = {}) => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationKey: ["plane_key", "save"],
    mutationFn: (apiKey: string) => dataProvider.savePlaneKey(apiKey),
    onSuccess: (status) => {
      queryClient.setQueryData(PLANE_KEY_QUERY_KEY, status);
      notify("crm.plane.key.saved", { type: "success" });
      onSuccess?.();
    },
    // No toast: the card shows the reason next to the field, where it can be
    // read while fixing it.
  });

  return { mutation };
};

export const useM_PlaneKey_Remove = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationKey: ["plane_key", "remove"],
    mutationFn: () => dataProvider.removePlaneKey(),
    onSuccess: (status) => {
      queryClient.setQueryData(PLANE_KEY_QUERY_KEY, status);
      notify("crm.plane.key.removed", { type: "info" });
    },
    onError: () => {
      notify("crm.plane.key.error_generic", { type: "error" });
    },
  });

  return { mutation };
};
